import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { authApi } from "../../services/auth";
import { useCart } from "../../contexts/CartProvider";
import { cartSyncGuestToUser } from "../../api/cartApi";
import {
  Eye,
  EyeOff,
  Mail,
  LockKeyhole,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { signInWithPopup } from "firebase/auth";
import { fbAuth, googleProvider } from "../../lib/firebase";

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function isAdminUser(u: any) {
  if (!u) return false;

  if (u.role && String(u.role).toLowerCase() === "admin") return true;
  if (u.isAdmin === true) return true;
  if (u.roleName && String(u.roleName).toLowerCase() === "admin") return true;
  if (u.userType && String(u.userType).toLowerCase() === "admin") return true;

  if (Array.isArray(u.roles) && typeof u.roles[0] !== "object") {
    return u.roles.some((r: any) => String(r).toLowerCase() === "admin");
  }

  if (Array.isArray(u.roles) && typeof u.roles[0] === "object") {
    return u.roles.some((r: any) => {
      const v = r?.code ?? r?.name ?? r?.role ?? "";
      return String(v).toLowerCase() === "admin";
    });
  }

  return false;
}

/** ✅ CRA chỉ inject env bắt đầu bằng REACT_APP_ */
const RAW_ADMIN_APP_URL =
  process.env.REACT_APP_ADMIN_APP_URL ||
  process.env.REACT_APP_ADMIN_URL ||
  process.env.REACT_APP_ADMIN_FRONTEND_URL ||
  "";

const ADMIN_APP_URL = String(RAW_ADMIN_APP_URL).replace(/\/+$/, "");

/** ✅ payload qua hash: không lộ qua query + không gửi lên server */
function buildAdminRedirectHash(payload: any) {
  const encoded = encodeURIComponent(btoa(JSON.stringify(payload)));
  return `#${encoded}`;
}

function getFrom(location: ReturnType<typeof useLocation>) {
  const stateFrom = (location.state as any)?.from;
  const qsFrom = new URLSearchParams(location.search).get("from");
  return (stateFrom || qsFrom || "").toString();
}

/** normalize đường dẫn admin */
function normalizeAdminPath(path: string) {
  if (!path) return "/dashboard";
  return path.startsWith("/") ? path : `/${path}`;
}

/** TRUE nếu from là route admin */
function isAdminRoute(path: string) {
  return path === "/dashboard" || path.startsWith("/dashboard/");
}

export default function LoginForm() {
  const { t } = useTranslation();

  const { setUser, user } = useAuth() as any;
  const { reloadCart } = useCart();

  const navigate = useNavigate();
  const location = useLocation();

  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);

  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState<{
    usernameOrEmail?: string;
    password?: string;
    form?: string;
  }>({});

  const inputBase =
    "w-full h-12 rounded-[18px] border bg-brand-ivory/70 backdrop-blur-md " +
    "px-4 text-sm outline-none transition " +
    "placeholder:text-brand-brown/35";

  const inputWithIcon = "pl-11";
  const inputError = "border-red-500/35 focus:border-red-500/55";
  const inputNormal =
    "border-brand-brown/10 focus:border-brand-brown/30 hover:border-brand-brown/20";

  const canSubmit = useMemo(() => {
    return usernameOrEmail.trim().length > 0 && password.length > 0 && !loading;
  }, [usernameOrEmail, password, loading]);

  const validate = () => {
    const e: typeof errors = {};
    const v = usernameOrEmail.trim();

    if (!v) e.usernameOrEmail = t("login.errors.usernameOrEmailRequired");
    if (!password) e.password = t("login.errors.passwordRequired");

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /** ✅ Redirect sang admin app (localhost:3001) + truyền token/user */
  function redirectToAdminApp(path: string, payload: any) {
    if (!ADMIN_APP_URL) return false;

    const p = normalizeAdminPath(path);
    const hash = buildAdminRedirectHash(payload);

    // admin app cần có route /auth-redirect để nhận hash rồi set token
    window.location.assign(
      `${ADMIN_APP_URL}/auth-redirect${hash}&to=${encodeURIComponent(p)}`,
    );
    return true;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setErrors((prev) => ({ ...prev, form: undefined }));
    if (!validate()) return;

    try {
      setLoading(true);

      // ✅ data dự kiến: { token, user }
      const data = await authApi.login(
        usernameOrEmail.trim(),
        password,
        rememberMe,
      );

      if (data?.user) setUser(data.user);

      try {
        await cartSyncGuestToUser();
      } catch (e) {
        console.warn("cartSyncGuestToUser skipped/failed:", e);
      }

      try {
        await reloadCart();
      } catch (e) {
        console.warn("reloadCart failed:", e);
      }

      const currentUser = data?.user ?? user;
      const admin = isAdminUser(currentUser);

      const from = getFrom(location);

      // ✅ Nếu có from
      if (from) {
        // from là admin route
        if (isAdminRoute(from)) {
          if (!admin) {
            navigate("/", { replace: true });
            return;
          }

          // admin -> đi sang admin app
          const ok = redirectToAdminApp(from, {
            token: data?.token,
            user: currentUser,
          });

          // fallback: nếu chưa set ADMIN_APP_URL thì chạy admin trong app hiện tại
          if (!ok) navigate(from, { replace: true });
          return;
        }

        // from thường
        navigate(from, { replace: true });
        return;
      }

      // ✅ Không có from
      if (admin) {
        const ok = redirectToAdminApp("/dashboard", {
          token: data?.token,
          user: currentUser,
        });

        if (!ok) navigate("/dashboard", { replace: true });
        return;
      }

      navigate("/", { replace: true });
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.response?.data?.error ||
        t("login.errors.invalidCredentials");

      setErrors((prev) => ({ ...prev, form: apiMessage }));
    } finally {
      setLoading(false);
    }
  };

  const onGoogleLogin = async () => {
    if (loading) return;

    setErrors((prev) => ({ ...prev, form: undefined }));

    try {
      setLoading(true);

      // 1) Đăng nhập Google bằng Firebase (popup)
      const cred = await signInWithPopup(fbAuth, googleProvider);

      // 2) Lấy Firebase ID Token
      const idToken = await cred.user.getIdToken(true);

      // 3) Gửi lên backend để backend trả JWT hệ thống { token, user }
      const data = await authApi.googleLogin(idToken, rememberMe);

      if (data?.user) setUser(data.user);

      try {
        await cartSyncGuestToUser();
      } catch (e) {
        console.warn("cartSyncGuestToUser skipped/failed:", e);
      }

      try {
        await reloadCart();
      } catch (e) {
        console.warn("reloadCart failed:", e);
      }

      const currentUser = data?.user ?? user;
      const admin = isAdminUser(currentUser);
      const from = getFrom(location);

      // ✅ y chang logic login thường
      if (from) {
        if (isAdminRoute(from)) {
          if (!admin) {
            navigate("/", { replace: true });
            return;
          }

          const ok = redirectToAdminApp(from, {
            token: data?.token,
            user: currentUser,
          });

          if (!ok) navigate(from, { replace: true });
          return;
        }

        navigate(from, { replace: true });
        return;
      }

      if (admin) {
        const ok = redirectToAdminApp("/dashboard", {
          token: data?.token,
          user: currentUser,
        });

        if (!ok) navigate("/dashboard", { replace: true });
        return;
      }

      navigate("/", { replace: true });
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.message ||
        t("login.errors.googleLoginFailed");

      setErrors((prev) => ({ ...prev, form: apiMessage }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* USERNAME OR EMAIL */}
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/55">
          {t("login.usernameOrEmailLabel")}
        </p>

        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-brown/40" />
          <input
            type="text"
            placeholder={t("login.usernameOrEmailPlaceholder")}
            value={usernameOrEmail}
            onChange={(e) => {
              setUsernameOrEmail(e.target.value);
              if (errors.usernameOrEmail)
                setErrors((m) => ({ ...m, usernameOrEmail: undefined }));
              if (errors.form) setErrors((m) => ({ ...m, form: undefined }));
            }}
            autoComplete="username"
            className={cn(
              inputBase,
              inputWithIcon,
              errors.usernameOrEmail ? inputError : inputNormal,
            )}
          />
        </div>

        {errors.usernameOrEmail && (
          <p className="text-xs text-red-600/80">{errors.usernameOrEmail}</p>
        )}
      </div>

      {/* PASSWORD */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/55">
            {t("login.passwordLabel")}
          </p>

          <Link
            to="/auth/forgot-password"
            className="text-[10px] uppercase tracking-[0.45em] text-brand-brown/45 hover:text-brand-brown transition"
          >
            {t("login.forgot")}
          </Link>
        </div>

        <div className="relative">
          <LockKeyhole className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-brown/40" />

          <input
            type={showPw ? "text" : "password"}
            placeholder="••••••••"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (errors.password) setErrors((m) => ({ ...m, password: undefined }));
              if (errors.form) setErrors((m) => ({ ...m, form: undefined }));
            }}
            autoComplete="current-password"
            className={cn(
              inputBase,
              inputWithIcon,
              "pr-12",
              errors.password ? inputError : inputNormal,
            )}
          />

          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center rounded-[14px]
                       border border-brand-brown/10 hover:border-brand-brown/25 hover:bg-brand-brown/5 transition"
            aria-label={showPw ? t("login.hidePassword") : t("login.showPassword")}
          >
            {showPw ? (
              <EyeOff className="w-4 h-4 text-brand-brown/55" />
            ) : (
              <Eye className="w-4 h-4 text-brand-brown/55" />
            )}
          </button>
        </div>

        {errors.password && (
          <p className="text-xs text-red-600/80">{errors.password}</p>
        )}
      </div>

      {/* REMEMBER ME */}
      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={rememberMe}
          onChange={(e) => setRememberMe(e.target.checked)}
          className="h-4 w-4 rounded border-brand-brown/20"
        />
        <span className="text-xs text-brand-brown/55">
          {t("login.rememberMe")}
        </span>
      </label>

      {/* FORM ERROR */}
      {errors.form && (
        <div className="rounded-[18px] border border-red-500/20 bg-red-500/5 px-4 py-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-red-600/80 mt-0.5" />
            <p className="text-sm text-red-600/80 leading-relaxed">{errors.form}</p>
          </div>
        </div>
      )}

      {/* PRIMARY CTA */}
      <button
        type="submit"
        disabled={!canSubmit}
        className={cn(
          "w-full h-12 rounded-[18px] transition",
          "text-[10px] uppercase tracking-[0.55em]",
          loading
            ? "bg-brand-brown/35 text-brand-ivory/80 cursor-not-allowed"
            : canSubmit
              ? "bg-brand-brown text-brand-ivory hover:opacity-95"
              : "bg-brand-brown/15 text-brand-brown/40 cursor-not-allowed",
        )}
      >
        {loading ? (
          <span className="inline-flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t("login.signingIn")}
          </span>
        ) : (
          t("login.signIn")
        )}
      </button>

      {/* DIVIDER */}
      <div className="flex items-center gap-4">
        <div className="h-px flex-1 bg-brand-brown/10" />
        <span className="text-[10px] uppercase tracking-[0.45em] text-brand-brown/35">
          {t("login.or")}
        </span>
        <div className="h-px flex-1 bg-brand-brown/10" />
      </div>

      {/* GOOGLE */}
      <button
        type="button"
        onClick={onGoogleLogin}
        disabled={loading}
        className={cn(
          "w-full h-12 rounded-[18px] border transition",
          "text-[10px] uppercase tracking-[0.45em]",
          loading
            ? "border-brand-brown/10 text-brand-brown/30 cursor-not-allowed"
            : "border-brand-brown/12 text-brand-brown/60 hover:text-brand-brown hover:border-brand-brown/25 hover:bg-brand-brown/5",
        )}
      >
        {t("login.continueWithGoogle")}
      </button>

      {/* BOTTOM LINKS */}
      <div className="pt-2 text-center">
        <Link
          to="/auth?tab=register"
          className="text-[10px] uppercase tracking-[0.45em] text-brand-brown/45 hover:text-brand-brown transition"
        >
          {t("login.createAccountLink")}
        </Link>
      </div>
    </form>
  );
}
